import React, { useState } from 'react';
import { Button } from '@primereact/ui/button';

import EsConfirmDelete from './EsConfirmDelete';
import { useEsTheme } from './EsThemeContext';
import { useEsStrings } from './EsStrings';

/**
 * Reset colors, in the palette menu and on the Colors page: back to the default colorsheet,
 * whichever it is now, for everyone alike -- the draft, the picked sheet, and an account's
 * own colors go, behind the house delete gate. An admin's reset is their own view: the
 * site's defaults change only by saving the default sheet or making another the default.
 * Disabled when the view is already the default sheet, unchanged.
 *
 * `reachable` is the palette menu's: its panel stays mounted to fade, so a closed one
 * must keep this out of the tab order. `label` is the Colorsheet page's, where the
 * heading already says colors.
 */
export default function EsResetColors( { 'data-testid': dataTestId, reachable = true, size, label, onDone } ) {
    const t = useEsStrings();
    const { atDefault, defaultSheet, resetColors } = useEsTheme();
    const [ open, setOpen ] = useState( false );
    const [ busy, setBusy ] = useState( false );
    const [ error, setError ] = useState( null );

    const aWhat = defaultSheet?.name
        ? t( 'common-reset-colors-what-named', 'Back to the default colorsheet, {name}.', { name: defaultSheet.name } )
        : t( 'common-reset-colors-what', 'Back to the default colorsheet.' );

    const confirm = () => {
        setBusy( true );
        setError( null );
        resetColors()
            .then( () => {
                setOpen( false );
                if ( onDone ) onDone();
            } )
            .catch( ( pError ) => setError( pError?.message || t( 'common-reset-colors-failed', 'The colors could not be reset just now.' ) ) )
            .finally( () => setBusy( false ) );
    };

    return (
        <>
            <Button data-testid={ dataTestId }
                    severity="secondary" variant="outlined" size={ size }
                    tabIndex={ reachable ? undefined : -1 }
                    disabled={ atDefault }
                    onClick={ () => { setError( null ); setOpen( true ); } }>
                { label ?? t( 'common-reset-colors', 'Reset colors' ) }
            </Button>
            <EsConfirmDelete data-testid={ dataTestId && `${ dataTestId }-dialog` }
                             open={ open }
                             title={ t( 'common-reset-colors-title', 'Reset colors?' ) }
                             what={ error ? aWhat + ' ' + error : aWhat }
                             confirm={ t( 'common-reset-colors-confirm', 'Reset' ) }
                             busy={ busy }
                             onConfirm={ confirm }
                             onCancel={ () => setOpen( false ) } />
        </>
    );
}
